/**
 * ─────────────────────────────────────────────────────────────
 * SITE CONFIG — static, developer-owned settings.
 *
 * This is the compile-time layer: brand, contact, currency, and
 * *default* opening hours / services. Things the business owner needs
 * to change without a redeploy live in the editable content layer
 * instead (see config/content.registry.ts + lib/content.ts).
 *
 * Rule of thumb:
 *   - changes rarely, set by the developer  → here
 *   - changes often, edited by the owner     → content collection
 * ─────────────────────────────────────────────────────────────
 */
import type { OpeningHour } from '@/lib/types';

export interface SiteConfig {
  name: string;
  legalName: string;
  tagline: string;
  description: string;
  /** Absolute URL of the deployed site (overridden by NEXT_PUBLIC_SITE_URL). */
  url: string;
  locale: string;

  contact: {
    email: string;
    phone: string;
    address: string;
    city: string;
    postalCode: string;
    country: string;
    geo?: { lat: number; lng: number };
  };

  social: {
    facebook?: string;
    instagram?: string;
    twitter?: string;
  };

  /** Brand palette — fed straight into the MUI theme. */
  brand: {
    primary: string;
    secondary: string;
    mode: 'light' | 'dark';
  };

  /** Default opening hours. The owner can override these live via the
   *  `hours` content slot; use resolveHours() to read the effective value. */
  openingHours: OpeningHour[];

  /** Appointment booking defaults. */
  booking: {
    slotMinutes: number;
    horizonDays: number;
    services: string[];
  };

  /** Currency for product prices (ISO 4217). */
  currency: string;
}

export const siteConfig: SiteConfig = {
  name: 'Épicerie des Alpes',
  // The enseigne and the Google listing both say "Épicerie des Alpes". The
  // registered raison sociale is NOT confirmed (the shop changed hands in
  // 01/2025) — the onboarding legal step makes the owner supply the real one
  // before the site can be published. Do not guess it here.
  legalName: 'Épicerie des Alpes',
  // Verbatim off the awning, second line under the red rule.
  tagline: 'Super marché · Fruits et légumes',
  description:
    'Épicerie de quartier au 132 cours Berriat à Grenoble : fruits et légumes tous les jours, épicerie du quotidien, pâtisseries maison et point relais colis. Ouvert 7 jours sur 7 jusqu’à 21 h.',
  url: process.env.NEXT_PUBLIC_SITE_URL || 'https://www.epicerie-des-alpes.fr',
  locale: 'fr_FR',

  contact: {
    // ⚠ Phone + email come from independent directories, NOT from the shop's own
    // Google listing, and the business changed hands in January 2025. To confirm
    // with the owner before publication (see docs/site-design.md, open questions).
    email: 'Epiceriedesalpes38@gmail.com',
    phone: '07 45 94 57 15',
    address: '132 cours Berriat',
    city: 'Grenoble',
    postalCode: '38000',
    country: 'FR',
    geo: { lat: 45.1887488, lng: 5.7092502 },
  },

  // A Facebook page seems to exist but could not be read without an account, and
  // no Instagram was found — nothing is linked until the owner confirms.
  social: {},

  brand: {
    primary: '#E03127', // le rouge de la règle sous l’enseigne / des paniers
    secondary: '#C9A227', // l’or des plateaux de la pâtisserie
    mode: 'dark',
  },

  // Google Business Profile hours, 7 j/7 with a midday break. Two entries per day
  // model the break. Kept in sync with the `hours` content slot default.
  openingHours: [
    { day: 0, open: '10:00', close: '13:30' },
    { day: 0, open: '15:00', close: '21:00' },
    { day: 1, open: '09:00', close: '13:30' },
    { day: 1, open: '15:00', close: '21:00' },
    { day: 2, open: '09:00', close: '13:30' },
    { day: 2, open: '15:00', close: '21:00' },
    { day: 3, open: '09:00', close: '13:30' },
    { day: 3, open: '15:00', close: '21:00' },
    { day: 4, open: '09:00', close: '13:30' },
    { day: 4, open: '15:00', close: '21:00' },
    { day: 5, open: '09:00', close: '12:00' },
    { day: 5, open: '15:00', close: '21:00' },
    { day: 6, open: '10:00', close: '13:30' },
    { day: 6, open: '15:00', close: '21:00' },
  ],

  // The appointments module is off (a supermarket takes no appointments); these
  // defaults only exist because the config shape requires them.
  booking: {
    slotMinutes: 30,
    horizonDays: 30,
    services: [],
  },

  currency: 'EUR',
};

export default siteConfig;
